import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
    ArrowRight, Check, Code2, Compass, Gamepad2, Gauge, Globe2,
    MessageSquare, MoveUpRight, RefreshCw, Search, ShieldCheck, Star, Users,
} from 'lucide-react';
import { publicApi } from '@/lib/api';
import { forumPublicApi } from '@/lib/forumApi';
import { AiImageDisclosure, isAiGeneratedAsset } from '@/components/AiImageDisclosure';

function setMeta(name, content) {
    let tag = document.head.querySelector(`meta[name="${name}"]`);
    if (!tag) {
        tag = document.createElement('meta');
        tag.setAttribute('name', name);
        document.head.appendChild(tag);
    }
    tag.setAttribute('content', content);
}

const webServices = [
    ['Strategie & Struktur', 'Ziele, Zielgruppen und Inhalte werden vor dem ersten Layout geklärt.'],
    ['Individuelles Webdesign', 'Ein Auftritt mit eigener Haltung – ohne vorgefertigtes Theme.'],
    ['Entwicklung & CMS', 'Schnell, responsiv und so gebaut, dass Inhalte pflegbar bleiben.'],
    ['Betrieb & Weiterentwicklung', 'Messung, Wartung und Verbesserungen nach dem Launch.'],
];

const seoChecks = [
    [Gauge, 'Technik', 'Indexierung, Performance und saubere Seitenstruktur'],
    [Search, 'Inhalte', 'Suchintention, Themenarchitektur und verständliche Texte'],
    [Globe2, 'Lokal', 'Sichtbarkeit für Unternehmen und Angebote in Ihrer Region'],
];

const methodSteps = [
    [Search, 'Analyse', 'Ziele, Nutzer und Engpässe werden konkret.', 'Fundierte Ausgangslage'],
    [Compass, 'Richtung', 'Inhalt, Design und Technik folgen einer gemeinsamen Idee.', 'Klare Projektlinie'],
    [Code2, 'Umsetzung', 'Wir entwickeln, testen und erklären jede wichtige Entscheidung.', 'Geprüfte Lösung'],
    [RefreshCw, 'Weiterentwicklung', 'Nach dem Launch bleiben wir ansprechbar und messen, was funktioniert.', 'Verlässlicher Betrieb'],
];

const modDevelopment = [
    [Compass, 'Machbarkeit & Schnittstellen', 'Spiel, SDK und erlaubte Modding-Wege werden vor der Entwicklung sauber geprüft.'],
    [Gamepad2, 'Gameplay & Zustände', 'Mechaniken, Regeln und relevante Spielzustände werden klar abgegrenzt umgesetzt.'],
    [RefreshCw, 'Multiplayer & Serverlogik', 'Synchronisierung, Rollen und Verbindungen entstehen passend zum Netzwerkmodell.'],
    [Gauge, 'Tests & Diagnose', 'Reale Testszenarien, Fehlerprotokolle und nachvollziehbare Übergaben sichern den Build ab.'],
];

const gameServerAutomationPoints = [
    [ShieldCheck, 'Automatischer Sicherheitsstand'],
    [Code2, 'Konfiguration & Neustart'],
    [Gauge, 'Externe Erreichbarkeitsprüfung'],
    [RefreshCw, 'Automatischer Rollback bei Fehlern'],
];

const fallbackForumBoards = [
    { slug: 'seo-sichtbarkeit', title: 'SEO & Sichtbarkeit', description: 'Analysen, Rankings und lokale Auffindbarkeit' },
    { slug: 'webentwicklung', title: 'Webentwicklung', description: 'Konzeption, Design und technische Fragen' },
    { slug: 'softwareentwicklung', title: 'Softwareentwicklung', description: 'APIs, Automatisierung und individuelle Lösungen' },
    { slug: 'fragen-antworten', title: 'Fragen & Antworten', description: 'Direkte Hilfe aus der Community' },
];

const defaultHomepage = {
    hero: {
        headline: 'Websites, die Vertrauen schaffen und Kunden gewinnen.',
        subheadline: 'Strategie, Design und Entwicklung für Unternehmen, die digital nicht wie alle anderen aussehen wollen – ergänzt durch SEO, das nachvollziehbar arbeitet.',
        buttonText: 'Webprojekt besprechen',
        buttonLink: '/kontakt',
        backgroundImage: '/images/studio-web-seeschloss-live-v5.webp',
    },
    cta: {
        headline: 'Machen wir Ihre Website zur besten Antwort.',
        subheadline: 'Bereit für einen Auftritt mit eigener Haltung?',
        buttonText: 'Erstgespräch anfragen',
        buttonLink: '/kontakt',
    },
    sections: [],
    partners: [],
};

export function HomePage() {
    const [reviews, setReviews] = useState([]);
    const [homepage, setHomepage] = useState(defaultHomepage);
    const [forumBoards, setForumBoards] = useState(fallbackForumBoards);
    const [forumRecent, setForumRecent] = useState([]);
    const [faqs, setFaqs] = useState([]);
    const [gameServerAutomationEnabled, setGameServerAutomationEnabled] = useState(false);

    useEffect(() => {
        document.title = 'Core Labs – Premium Websites, SEO & Software aus Schleswig-Holstein';
        setMeta('description', 'Core Labs entwickelt individuelle Websites, verbessert die Sichtbarkeit bei Google und realisiert Software sowie Multiplayer-Mods – persönlich aus Schleswig-Holstein.');

        const schema = document.createElement('script');
        schema.type = 'application/ld+json';
        schema.dataset.coreLabsSchema = 'true';
        schema.text = JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'ProfessionalService',
            '@id': 'https://core-labs.de/#business',
            name: 'Core Labs',
            url: 'https://core-labs.de/',
            logo: 'https://core-labs.de/core-labs-logo-transparent.png',
            image: 'https://core-labs.de/og.png',
            email: 'info@core-labs.de',
            areaServed: { '@type': 'Country', name: 'Deutschland' },
            address: {
                '@type': 'PostalAddress',
                streetAddress: 'Fischerstraße 10',
                postalCode: '23730',
                addressLocality: 'Neustadt in Holstein',
                addressRegion: 'Schleswig-Holstein',
                addressCountry: 'DE',
            },
            serviceType: ['Webentwicklung', 'Suchmaschinenoptimierung', 'Softwareentwicklung', 'Multiplayer-Mod-Entwicklung'],
        });
        document.head.appendChild(schema);
        return () => schema.remove();
    }, []);

    useEffect(() => {
        publicApi.getGameServerReviews(3).then(response => {
            const items = Array.isArray(response.data) ? response.data : response.data?.items || [];
            setReviews(items.slice(0, 3));
        }).catch(() => {});

        publicApi.getFaqs().then(response => {
            const items = Array.isArray(response.data) ? response.data : [];
            setFaqs(items.slice(0, 6));
        }).catch(() => {});

        publicApi.getGameServerTemplates().then(response => {
            const items = Array.isArray(response.data) ? response.data : response.data?.items || [];
            setGameServerAutomationEnabled(items.some(item => item?.automated_mutations_enabled === true));
        }).catch(() => setGameServerAutomationEnabled(false));
    }, []);

    useEffect(() => {
        publicApi.getHomepageContent().then(response => {
            const data = response.data || {};
            setHomepage({
                ...defaultHomepage,
                ...data,
                hero: { ...defaultHomepage.hero, ...(data.hero || {}) },
                cta: { ...defaultHomepage.cta, ...(data.cta || {}) },
                sections: Array.isArray(data.sections) ? data.sections : [],
                partners: Array.isArray(data.partners) ? data.partners : [],
            });
        }).catch(() => {});
    }, []);

    useEffect(() => {
        Promise.all([forumPublicApi.getCategories(), forumPublicApi.getRecentPosts(4)]).then(([categoriesResponse, recentResponse]) => {
            const boards = (categoriesResponse.data || []).flatMap(category => category.boards || []);
            if (boards.length) setForumBoards(boards.slice(0, 4));
            setForumRecent((recentResponse.data || []).slice(0, 4));
        }).catch(() => {});
    }, []);

    const sectionVisible = id => homepage.sections.find(section => section.id === id)?.enabled !== false;
    const enabledPartners = homepage.partners.filter(partner => partner.enabled !== false);
    const headlineParts = homepage.hero.headline.split(/(Vertrauen)/i);
    const heroImage = homepage.hero.backgroundImage || '/images/studio-web-seeschloss-live-v5.webp';
    const heroImageIsAiGenerated = isAiGeneratedAsset(heroImage);

    return (
        <div className="cl-site cl-studio" data-testid="home-page">
            <section className="studio-hero" aria-labelledby="hero-title">
                <div className="studio-hero-copy">
                    <p className="studio-eyebrow"><span>Core Labs</span> Digitalstudio · Neustadt in Holstein</p>
                    <h1 id="hero-title">{headlineParts.map((part, index) => /^Vertrauen$/i.test(part) ? <em key={index}>{part}</em> : part)}</h1>
                    <p className="studio-lead">{homepage.hero.subheadline}</p>
                    <div className="studio-actions">
                        <Link className="studio-button studio-button-accent" to={homepage.hero.buttonLink || '/kontakt'}>{homepage.hero.buttonText} <ArrowRight aria-hidden="true" /></Link>
                        <Link className="studio-link" to="/leistungen">Leistungen & Preise <MoveUpRight aria-hidden="true" /></Link>
                    </div>
                    <ul className="studio-assurances" aria-label="Qualitätsmerkmale">
                        <li><ShieldCheck /> DSGVO mitgedacht</li>
                        <li><Gauge /> Performance von Anfang an</li>
                        <li><Globe2 /> Technische SEO-Basis</li>
                    </ul>
                </div>

                <figure className="studio-hero-media" data-ai-generated={heroImageIsAiGenerated ? 'true' : undefined}>
                    <img src={heroImage} alt="Webdesigner bei der Prüfung der Website des See Schlosshotels am Kellersee" width="1536" height="1024" fetchPriority="high" />
                    {heroImageIsAiGenerated && <AiImageDisclosure />}
                    <figcaption>
                        <strong>Strategie · Design · Code</strong>
                    </figcaption>
                    <div className="studio-media-note" aria-hidden="true"><i />Strategie & Umsetzung<br />aus einer Hand</div>
                </figure>
            </section>

            <div className="studio-capabilities" aria-label="Kernleistungen">
                <span>Webdesign</span><span>Webentwicklung</span><span>SEO</span><span>CMS</span><span>Betreuung</span>
            </div>

            {sectionVisible('websites') && <section id="websites" className="studio-web" aria-labelledby="websites-title">
                <header className="studio-section-head">
                    <div className="studio-section-title">
                        <p className="studio-index">Websites & Onlineshops</p>
                        <h2 id="websites-title">Kein Baukasten.<br /><em>Ein digitaler Auftritt mit Substanz.</em></h2>
                    </div>
                    <div className="studio-section-copy">
                        <p>Ihre Website muss in wenigen Sekunden erklären, warum Kunden bei Ihnen richtig sind. Wir verbinden klare Inhalte mit einem eigenständigen Design und einer technischen Basis, die langfristig funktioniert.</p>
                        <Link className="studio-link" to="/leistungen/business-website">Business-Website ansehen <MoveUpRight aria-hidden="true" /></Link>
                    </div>
                </header>

                <div className="studio-web-grid">
                    <figure className="studio-web-showcase" data-ai-generated="true">
                        <img src="/images/website-design-review-meeting-room-v5.jpg" alt="Webdesigner bei einer professionellen Layoutprüfung in einem modernen Meetingraum" width="1536" height="1024" loading="lazy" />
                        <AiImageDisclosure />
                        <figcaption>
                            <span>Individuelles Webdesign</span>
                            <strong>Von der ersten Idee bis zum fertigen Auftritt.</strong>
                        </figcaption>
                    </figure>

                    <div className="studio-web-services">
                        <header>
                            <span>Was Sie bekommen</span>
                            <strong>Ein klar geführtes Webprojekt.</strong>
                        </header>
                        <ul className="studio-service-list">
                            {webServices.map(([title, description]) => (
                                <li key={title}>
                                    <div><h3>{title}</h3><p>{description}</p></div>
                                    <Check aria-hidden="true" />
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            </section>}

            {sectionVisible('seo') && <section id="seo" className="studio-seo" aria-labelledby="seo-title">
                <div className="studio-seo-inner">
                    <header className="studio-seo-head">
                        <div>
                            <p className="studio-index">SEO & Google</p>
                            <h2 id="seo-title">Gefunden werden,<br /><em>wenn Kunden suchen.</em></h2>
                        </div>
                        <p>Wir verbinden Suchanfragen, Klicks, technische Signale und lokale Sichtbarkeit. Daraus entsteht eine klare Maßnahmenliste – nachvollziehbar priorisiert statt nach Bauchgefühl.</p>
                    </header>

                    <div className="studio-seo-stage">
                        <div className="studio-seo-panel">
                            <header>
                                <span>Kostenlos · ohne Konto · mit PDF-Bericht</span>
                                <strong>Website prüfen, bevor Probleme teuer werden.</strong>
                            </header>
                            <div className="studio-checks">
                                {seoChecks.map(([Icon, title, text]) => (
                                    <article key={title}>
                                        <span className="studio-check-icon"><Icon aria-hidden="true" /></span>
                                        <div><h3>{title}</h3><p>{text}</p></div>
                                    </article>
                                ))}
                            </div>
                            <div className="studio-seo-actions">
                                <Link className="studio-button studio-button-light" to="/website-check">Kostenlosen Website-Check starten <ArrowRight aria-hidden="true" /></Link>
                                <Link className="studio-link" to="/leistungen/seo-audit">SEO-Audit ansehen <MoveUpRight aria-hidden="true" /></Link>
                                <Link className="studio-link" to="/leistungen/local-seo">Local SEO ansehen <MoveUpRight aria-hidden="true" /></Link>
                            </div>
                        </div>

                        <figure className="studio-seo-media" data-ai-generated="true">
                            <img src="/images/studio-seo-private-analytics-v3.jpg" alt="Anonymisierte SEO-Analyse mit verpixelten Kundendaten, Suchentwicklung und lokaler Sichtbarkeit" width="1536" height="1024" loading="lazy" />
                            <AiImageDisclosure />
                            <figcaption className="studio-audit-card">
                                <span>SEO · Performance · EU · Sicherheit</span>
                                <strong>Ein kostenloser Check. Klare Prioritäten.</strong>
                                <Link to="/website-check">Website jetzt prüfen <ArrowRight aria-hidden="true" /></Link>
                            </figcaption>
                        </figure>
                    </div>
                </div>
            </section>}

            {sectionVisible('community') && <section className="studio-community" aria-labelledby="community-title">
                <div className="studio-community-inner">
                    <header className="studio-community-head">
                        <div>
                            <p className="studio-index">Community & Support</p>
                            <h2 id="community-title">Wissen teilen.<br /><em>Gemeinsam weiterkommen.</em></h2>
                        </div>
                        <div className="studio-community-intro">
                            <p>Das Core Labs Forum verbindet Kunden, Entwickler und Spieler: Fragen klären, Lösungen dokumentieren, Themen abonnieren und Projekte nachvollziehbar weiterführen.</p>
                            <Link className="studio-button studio-button-light" to="/forum">Community öffnen <ArrowRight aria-hidden="true" /></Link>
                        </div>
                    </header>

                    <div className="studio-forum-preview">
                        <div className="studio-forum-top">
                            <span><MessageSquare aria-hidden="true" /> Core Labs Community</span>
                            <span className="studio-forum-status"><i /> Moderiert & öffentlich lesbar</span>
                            <Link to="/forum">Forum öffnen <ArrowRight aria-hidden="true" /></Link>
                        </div>

                        <div className="studio-forum-dashboard">
                            <aside className="studio-forum-boards">
                                <header><span>Forum-Bereiche</span><small>Direkt ins passende Thema</small></header>
                                <nav aria-label="Forum-Bereiche">
                                    {forumBoards.map(board => (
                                        <Link to={`/forum/${board.slug}`} key={board.id || board.slug}>
                                            <span className="studio-forum-board-icon"><MessageSquare aria-hidden="true" /></span>
                                            <span><strong>{board.title}</strong><small>{board.description}</small></span>
                                            <ArrowRight aria-hidden="true" />
                                        </Link>
                                    ))}
                                </nav>
                                <Link className="studio-forum-profile" to="/forum/me"><Users aria-hidden="true" /><span><strong>Mein Forum</strong><small>Abonnements, Beiträge und Nachrichten</small></span><ArrowRight aria-hidden="true" /></Link>
                            </aside>

                            <section className="studio-forum-feed" aria-labelledby="forum-feed-title">
                                <header>
                                    <div><span>Aus der Community</span><h3 id="forum-feed-title">Aktuelle Diskussionen</h3></div>
                                    <Link to="/forum">Alle Themen <ArrowRight aria-hidden="true" /></Link>
                                </header>
                                <div className="studio-forum-feed-list">
                                    {forumRecent.length > 0 ? forumRecent.map(post => (
                                        <Link to={`/forum/t/${post.thread_slug}`} key={post.id}>
                                            <span className="studio-forum-avatar" aria-hidden="true">{(post.author_name || 'C').slice(0, 1).toUpperCase()}</span>
                                            <span><strong>{post.thread_title}</strong><small>{post.author_name || 'Community'}{post.created_at ? ` · ${new Date(post.created_at).toLocaleDateString('de-DE', { day: '2-digit', month: 'short' })}` : ''}</small></span>
                                            <ArrowRight aria-hidden="true" />
                                        </Link>
                                    )) : (
                                        <Link className="studio-forum-empty" to="/forum">
                                            <MessageSquare aria-hidden="true" />
                                            <span><strong>Aktuelle Themen im Forum ansehen</strong><small>Fragen, Lösungen und Projektwissen aus der Community.</small></span>
                                            <ArrowRight aria-hidden="true" />
                                        </Link>
                                    )}
                                </div>
                                <footer><ShieldCheck aria-hidden="true" /> Schreiben mit frei gewähltem Forumsnamen</footer>
                            </section>
                        </div>
                    </div>
                </div>
            </section>}

            {sectionVisible('method') && <section className="studio-method" aria-labelledby="method-title">
                <div className="studio-method-inner">
                    <header className="studio-method-head">
                        <div>
                            <p className="studio-index">Zusammenarbeit</p>
                            <h2 id="method-title">Erst verstehen.<br /><em>Dann vereinfachen.</em></h2>
                        </div>
                        <p>Jeder Schritt hat ein klares Ergebnis. So bleiben Entscheidungen nachvollziehbar, Aufwand kalkulierbar und Ihr Projekt jederzeit steuerbar.</p>
                    </header>

                    <ol className="studio-method-track">
                        {methodSteps.map(([Icon, title, description, result]) => (
                            <li key={title}>
                                <span className="studio-method-icon"><Icon aria-hidden="true" /></span>
                                <div>
                                    <h3>{title}</h3>
                                    <p>{description}</p>
                                </div>
                                <span className="studio-method-result"><Check aria-hidden="true" />{result}</span>
                            </li>
                        ))}
                    </ol>

                    <footer className="studio-method-footer">
                        <div>
                            <span><Check aria-hidden="true" />Fester Ansprechpartner</span>
                            <span><Check aria-hidden="true" />Klare Freigaben</span>
                            <span><Check aria-hidden="true" />Saubere Übergabe</span>
                        </div>
                        <Link className="studio-link" to="/kontakt">Zusammenarbeit besprechen <MoveUpRight aria-hidden="true" /></Link>
                    </footer>
                </div>
            </section>}

            {sectionVisible('reviews') && reviews.length > 0 && (
                <section className="studio-reviews" aria-labelledby="reviews-title">
                    <header>
                        <p className="studio-index">Kundenstimmen</p>
                        <h2 id="reviews-title">Erfahrungen aus<br /><em>echten Gameservern.</em></h2>
                        <p>Bewertungen stammen aus tatsächlich bereitgestellten Gameserver-Instanzen. Zulässige Stimmen werden unabhängig von der Sternezahl und ohne Gegenleistung veröffentlicht; „verifiziert“ bedeutet, dass die zugehörige Instanz geprüft wurde.</p>
                    </header>
                    <div className="studio-review-list">
                        {reviews.map(review => {
                            const rating = Number(review.ratings?.average ?? review.rating) || 0;
                            return (
                            <article key={review.id}>
                                <div className="studio-review-stars" aria-label={`${rating.toLocaleString('de-DE', { maximumFractionDigits: 1 })} von 5 Sternen`}>
                                    {[1, 2, 3, 4, 5].map(value => <Star key={value} className={value <= rating ? 'is-active' : ''} aria-hidden="true" />)}
                                </div>
                                <h3>{review.title}</h3>
                                <blockquote>„{review.body}“</blockquote>
                                <footer><strong>{review.public_name}</strong>{(review.verified_customer || review.verified_label) && <span><ShieldCheck /> {review.verified_label || 'Verifizierter Kunde'}</span>}</footer>
                                {review.admin_response && <p className="studio-review-response"><b>Core Labs:</b> {review.admin_response}</p>}
                            </article>
                            );
                        })}
                    </div>
                </section>
            )}

            {sectionVisible('partners') && enabledPartners.length > 0 && <section id="partner" className="studio-partner" aria-labelledby="partner-title">
                <header className="studio-partner-intro">
                    <p className="studio-index">Partnernetzwerk</p>
                    <h2 id="partner-title">Ausgewählte<br /><em>Partner.</em></h2>
                    <p>Direkter Austausch, regionale Nähe und gemeinsame technische Interessen.</p>
                </header>
                <div className="studio-partner-list">
                    {enabledPartners.map(partner => {
                        const content = <>
                            <div className="studio-partner-visual"><img src={partner.logoUrl} alt={partner.alt || partner.name} loading="lazy" /></div>
                            <div className="studio-partner-copy"><strong>{partner.name}</strong><span>{partner.description}</span></div>
                        </>;
                        return partner.linkUrl ? (
                            <a key={partner.id} className="studio-partner-tile" href={partner.linkUrl} target="_blank" rel="noopener noreferrer" aria-label={`${partner.name}-Website in einem neuen Tab öffnen`}>{content}</a>
                        ) : (
                            <article key={partner.id} className="studio-partner-tile is-static" aria-label={partner.name}>{content}</article>
                        );
                    })}
                </div>
            </section>}

            {sectionVisible('software') && <section className="studio-engineering" aria-labelledby="engineering-title">
                <div className="studio-engineering-inner">
                    <header className="studio-engineering-head">
                        <div>
                            <p className="studio-index">Mod-Entwicklung & Multiplayer-Systeme</p>
                            <h2 id="engineering-title">Spielidee trifft<br /><span>Systemarchitektur.</span></h2>
                        </div>
                        <div className="studio-engineering-intro">
                            <span><Gamepad2 aria-hidden="true" />Individuell für unterstützte Spiele</span>
                            <p>Wir entwickeln individuelle Mods und Multiplayer-Erweiterungen – von der Machbarkeitsprüfung über Gameplay- und Netzwerklogik bis zu Tests, Dokumentation und technischer Übergabe.</p>
                        </div>
                    </header>

                    <figure className="studio-engineering-showcase" data-ai-generated="true">
                        <img src="/images/studio-mod-development-v4.webp" alt="Programmierstudio bei der Entwicklung und dem Test einer Multiplayer-Modifikation" width="1536" height="1024" loading="lazy" />
                        <AiImageDisclosure />
                    </figure>

                    <div className="studio-engineering-workbench">
                        <header>
                            <span>Unser Vorgehen</span>
                            <h3>Vom Mod-Konzept bis zum getesteten Build.</h3>
                            <p>Kein Standardpaket: Umfang und Architektur richten sich nach Spiel, Schnittstellen und gewünschter Mechanik.</p>
                        </header>
                        <div className="studio-engineering-services">
                            {modDevelopment.map(([Icon, title, description]) => (
                                <article key={title}><Icon aria-hidden="true" /><div><h4>{title}</h4><p>{description}</p></div></article>
                            ))}
                        </div>
                    </div>

                    <footer className="studio-engineering-footer">
                        <div>
                            <Link className="studio-link" to="/software">Produkte & Mod-Pakete <MoveUpRight aria-hidden="true" /></Link>
                            <Link className="studio-link" to="/kontakt">Mod-Projekt anfragen <ArrowRight aria-hidden="true" /></Link>
                        </div>
                    </footer>
                </div>
            </section>}

            {gameServerAutomationEnabled && <section className="studio-server-automation" aria-labelledby="server-automation-title" data-testid="home-gameserver-automation">
                <div className="studio-server-automation-inner">
                    <header>
                        <p className="studio-index">SERVERVERWALTUNG OHNE WARTEZEIT</p>
                        <h2 id="server-automation-title">Du änderst. Das System erledigt den Rest.</h2>
                        <p>Passe Ressourcen, unterstützte Spieleinstellungen und Spielversionen direkt im Kundenbereich an. Core Labs sichert deinen Server, übernimmt die Änderung, startet ihn neu und prüft anschließend die öffentliche Erreichbarkeit – automatisch und erst nach deiner Bestätigung.</p>
                        <div className="studio-server-automation-actions">
                            <Link className="studio-button studio-button-accent" to="/gameserver-leihen">Gameserver kostenlos testen <ArrowRight aria-hidden="true" /></Link>
                            <Link className="studio-link" to="/gameserver-leihen#automatische-serververwaltung">So funktioniert es <MoveUpRight aria-hidden="true" /></Link>
                        </div>
                    </header>
                    <ul>
                        {gameServerAutomationPoints.map(([Icon, label]) => <li key={label}><Icon aria-hidden="true" /><span>{label}</span></li>)}
                    </ul>
                    <footer><ShieldCheck aria-hidden="true" /><span>Gilt für unterstützte Spiele und Änderungen. Ein Neustart erfolgt nur nach deiner ausdrücklichen Bestätigung.</span></footer>
                </div>
            </section>}

            {sectionVisible('faq') && faqs.length > 0 && <section className="studio-faq" aria-labelledby="faq-title">
                <div className="studio-faq-inner">
                    <header className="studio-faq-head">
                        <p className="studio-index">Häufig gefragt</p>
                        <h2 id="faq-title">Vor dem Start.<br /><em>Klar beantwortet.</em></h2>
                        <p>Die wichtigsten Antworten zu Ablauf, Kosten, CMS, SEO und Datenschutz.</p>
                        <Link className="studio-link" to="/faq">Alle Fragen ansehen <MoveUpRight aria-hidden="true" /></Link>
                    </header>
                    <div className="studio-faq-list">
                        {faqs.map((faq, index) => (
                            <details key={faq.id || faq.question} open={index === 0}>
                                <summary><span>{faq.question}</span><i aria-hidden="true" /></summary>
                                <div><p>{faq.answer}</p></div>
                            </details>
                        ))}
                    </div>
                </div>
            </section>}

            {sectionVisible('cta') && <section className="studio-final-cta" aria-labelledby="final-cta-title">
                <div className="studio-final-cta-inner">
                    <header className="studio-final-cta-main">
                        <p className="studio-index">Projektanfrage</p>
                        <h2 id="final-cta-title">{homepage.cta.headline}</h2>
                    </header>
                    <div className="studio-final-copy">
                        <p>Ein kurzes Briefing reicht. Sie erhalten eine persönliche Rückmeldung mit einem sinnvollen nächsten Schritt.</p>
                        <a href="mailto:info@core-labs.de">info@core-labs.de</a>
                    </div>
                    <Link className="studio-final-action" to={homepage.cta.buttonLink || '/kontakt'}>{homepage.cta.buttonText}<MoveUpRight aria-hidden="true" /></Link>
                </div>
            </section>}
        </div>
    );
}

export default HomePage;
